<template>
	<v-dialog
		v-model="visible"
		:width="$vuetify.breakpoint.mdAndUp ? '480px' : null"
	>
		<v-card>
			<v-card-title>
				<device-icon
					v-if="device"
					class="pr-4"
					:device="device"
					:highlight="allSuccess || undefined"
				/>
				{{ name }}
			</v-card-title>

			<v-stepper v-model="testIdx" class="test-stepper" vertical>
				<template v-for="(test, i) in tests">
					<v-stepper-step
						:key="`${device.meta.guid}-${i}`"
						:step="i + 1"
						:complete="test.success"
						>{{ test.text }}</v-stepper-step
					>
					<v-stepper-content
						:key="`${device.meta.guid}-${i}-content`"
						:step="i + 1"
					>
						<v-alert :value="!!test.error" color="rust-red" text>
							<span class="body-2">{{ test.error }}</span>
						</v-alert>
					</v-stepper-content>
				</template>
			</v-stepper>

			<v-card-actions>
				<v-spacer />
				<v-btn
					color="ocean darken-1"
					:loading="testing"
					outlined
					@click="runTests"
				>
					Run Tests
					<v-icon right dark>fa-play-circle</v-icon>
				</v-btn>
				<v-btn outlined @click="close">Done</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>

<style lang="scss" scoped>
.test-stepper {
	box-shadow: none;
}
</style>

<script>
import _ from "lodash";
import { mapActions } from "vuex";

const data = () => ({
	testIdx: 0,
	tests: [],
	testing: false,
});

export default {
	props: {
		device: { type: Object, default: null },
	},

	data,

	computed: {
		visible: {
			get() {
				return !!this.device;
			},
			set(val) {
				if (!val) this.close();
			},
		},

		name() {
			return _.get(this.device, "name");
		},

		allSuccess() {
			return _.every(this.tests, { success: true });
		},
	},

	watch: {
		device(is, was) {
			// Loading tests for device
			this.loadDeviceTests(is);
		},
	},

	methods: {
		...mapActions({ doLoadDeviceTests: "install/loadDeviceTests" }),

		close() {
			this.$emit("close");
		},

		reset() {
			Object.assign(this, data());
		},

		async loadDeviceTests() {
			if (!this.device) {
				this.reset();
				return;
			}

			this.tests = await this.doLoadDeviceTests(this.device);
		},

		async runTests() {
			this.testIdx = 1;

			// Load device tests *again* to make sure we've cleared any
			// modified `success`, `error`, `testing` flags on the test object.
			this.tests = await this.doLoadDeviceTests(this.device);

			this.testing = true;
			for (let i = 0; i < this.tests.length; i++) {
				const test = this.tests[i];
				const { name, path } = test.device;

				test.testing = true;

				try {
					this.$log(`Live: Running test for '${name}' (${path})`);

					await test.run();

					// Mark as success
					test.success = true;

					// Go to next test
					this.testIdx = this.testIdx + 1;
				} catch (e) {
					this.success = false;

					const msg = _.isString(e) ? e : e.message;
					test.error = msg;

					this.$log(`Live: Error running test for '${name}' (${path}): ${e}`);

					// Stop here. Don't perform next test.
					break;
				} finally {
					test.testing = false;
				}
			}
			this.testing = false;
		},
	},
};
</script>
